// src/pages/admin/capacity/PartnersManager.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Input,
  Space,
  Button,
  message,
  Upload,
  Modal,
  Progress,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import { uploadOne } from "../../../utils/utils"; // must return a URL, accept onProgress

const rawFile = (f) => (f?.originFileObj ? f.originFileObj : f);

export default function PartnersManager({ projectId, onChange }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState({
    name: "",
    logo: "", // optional: pasted URL
    website: "",
    order: null,
  });

  // logo upload state (choose either paste URL or select file)
  const [logoFileList, setLogoFileList] = useState([]); // [{uid, name, originFileObj, ...}]
  const [logoPreview, setLogoPreview] = useState({
    open: false,
    src: "",
    title: "",
  });
  const [logoPct, setLogoPct] = useState(0);
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false); // guard double-click/StrictMode

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(routes.capacityAdminItem(projectId));
      setProject(data?.project || data);
    } catch (e) {
      message.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
  }, [projectId]);

  const resetDraft = () => {
    setDraft({ name: "", logo: "", website: "", order: null });
    setLogoFileList([]);
    setLogoPct(0);
  };

  const validateDraft = () => {
    if (!draft.name.trim()) {
      message.error("Name is required");
      return false;
    }
    // must have either a pasted URL or a selected file
    if (!draft.logo.trim() && logoFileList.length === 0) {
      message.error("Logo is required (paste URL or upload a file).");
      return false;
    }
    return true;
  };

  const add = async () => {
    if (!validateDraft()) return;
    if (addingRef.current) return;
    addingRef.current = true;

    try {
      setAdding(true);
      setLogoPct(0);

      // If user selected a file, upload it; else use pasted URL
      let logoUrl = draft.logo.trim();
      if (logoFileList[0]?.originFileObj) {
        const file = rawFile(logoFileList[0]);
        logoUrl = await uploadOne(file, setLogoPct); // onProgress → setLogoPct
        setLogoPct(100);
      }

      const payload = {
        name: draft.name.trim(),
        logo: logoUrl, // server expects "logo"
        website: draft.website.trim() || undefined,
        order:
          typeof draft.order === "number" && !Number.isNaN(draft.order)
            ? draft.order
            : undefined,
      };

      await axios.post(routes.capacityAdminPartnerAdd(projectId), payload);
      message.success("Partner added");
      resetDraft();
      onChange && onChange();
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Add failed");
    } finally {
      setAdding(false);
      addingRef.current = false;
    }
  };

  const remove = async (partnerId) => {
    try {
      await axios.delete(
        routes.capacityAdminPartnerDelete(projectId, partnerId)
      );
      onChange && onChange();
      load();
    } catch (e) {
      message.error("Delete failed");
    }
  };

  const columns = [
    {
      title: "Logo",
      width: 64,
      render: (_, r) =>
        r.logo ? (
          <img
            src={r.logo}
            alt=""
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        ) : (
          "—"
        ),
    },
    { title: "Name", dataIndex: "name" },
    { title: "Website", dataIndex: "website" },
    { title: "Order", dataIndex: "order", width: 80 },
    {
      title: "Actions",
      width: 100,
      render: (_, r) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => remove(r._id)}
        />
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Space wrap align="start">
          <Input
            placeholder="Name"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            style={{ width: 220 }}
          />

          <Input
            placeholder="Website URL"
            value={draft.website}
            onChange={(e) =>
              setDraft((d) => ({ ...d, website: e.target.value }))
            }
            style={{ width: 260 }}
          />

          <Input
            placeholder="Order (optional)"
            value={draft.order ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                order:
                  e.target.value === "" ? null : Number(e.target.value) || null,
              }))
            }
            style={{ width: 160 }}
          />

          <Upload
            listType="picture-card"
            fileList={logoFileList}
            onChange={({ fileList }) => setLogoFileList(fileList.slice(-1))} // keep only one
            beforeUpload={() => false} // prevent auto-upload
            onPreview={(file) =>
              setLogoPreview({
                open: true,
                src: file.url || file.thumbUrl,
                title: file.name || "Preview",
              })
            }
            maxCount={1}
            accept="image/*"
          >
            {logoFileList.length >= 1 ? null : (
              <div style={{ width: 100 }}>
                <UploadOutlined /> <div style={{ marginTop: 4 }}>Logo</div>
              </div>
            )}
          </Upload>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={add}
            loading={adding}
          >
            Add Partner
          </Button>
        </Space>

        {/* Show upload progress only during Add when a file is selected */}
        {adding && logoFileList.length > 0 && (
          <div style={{ width: 260 }}>
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>
              Uploading logo…
            </div>
            <Progress percent={logoPct} size="small" />
          </div>
        )}

        <Table
          rowKey={(r) => r._id}
          columns={columns}
          dataSource={project?.partners || []}
          loading={loading}
          pagination={false}
          size="small"
        />
      </Space>

      {/* Image preview modal */}
      <Modal
        open={logoPreview.open}
        footer={null}
        title={logoPreview.title}
        onCancel={() => setLogoPreview((p) => ({ ...p, open: false }))}
      >
        <img alt="preview" style={{ width: "100%" }} src={logoPreview.src} />
      </Modal>
    </>
  );
}
